import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useUserStore } from '@/stores/userStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useMatchStore, loadStarterSkin, type BurstFx, type FloatText } from '@/stores/matchStore';
import { useSkillsStore } from '@/stores/skillsStore';
import { useT } from '@/i18n/useI18n';
import { audio } from '@/audio/AudioService';
import { native } from '@/platform/native';
import { SkillIcon } from '@/art';
import { MatchTopBar } from './match/MatchTopBar';
import { LaneHeader } from './match/LaneHeader';
import { SkillButton } from './match/SkillButton';
import { PixiCanvas } from './match/PixiCanvas';
import type { MatchScene } from '@/game/scenes/MatchScene';
import { type Texture } from 'pixi.js';
import { useHoldSkinStore } from '@/holdskins/holdSkinStore';
import { loadHoldSkinTexture } from '@/holdskins/holdTexture';
import { resolveAssetUrl } from '@/shop/assetUrl';
import { TipTicker } from '@/match/TipTicker';
import { challengeApi, type ChallengeTapEvent } from '@/challenges/challengeApi';
import { reportGameCenterChallengeResult } from '@/challenges/gameCenterChallenge';
import { VideoFinisher } from '@/game/fx/VideoFinisher';
import { getPreloadedMedia, absoluteAssetUrl } from '@/net/assetPrefetch';
import { resolveMatchBackgroundTheme } from '@/game/matchBackground';
import { getSocket } from '@/net/socket';
import {
  AI_DEFAULT_SPEED_S,
  AI_SPEED_JITTER,
  HEADER_H,
  LANE_H,
  MATCH_OVER_OVERLAY_MS,
} from '@shared/gameConfig';
import type { ColorType } from '@shared/types';
import {
  EV,
  type MatchFall,
  type MatchCountdown,
  type MatchOver,
  type MatchSkillFired,
  type MatchStart,
  type MatchStep,
  type TeamMatchStep,
  type TeamMatchFall,
  type TeamSkillFired,
  type TeamMatchOver,
  type TeamMember,
  type CharacterSkin,
  type EquippedCosmetic,
} from '@shared/protocol';

// Cosmetic tier ordering — higher wins when picking a 2v2 lane's team skin.
const RARITY_ORDER: Record<string, number> = { common: 0, rare: 1, epic: 2, legendary: 3 };
const rarityRank = (r?: string | null): number => (r ? RARITY_ORDER[r] ?? 0 : -1);

/** Highest-rarity EQUIPPED character among a team (null if none equipped). */
function bestRarityCharacter(team: TeamMember[]): CharacterSkin | null {
  let best: CharacterSkin | null = null;
  for (const m of team) {
    if (m.character && rarityRank(m.character.rarity) > rarityRank(best?.rarity)) {
      best = m.character;
    }
  }
  return best;
}

/** Highest-rarity EQUIPPED hold-skin url among a team (null if none equipped). */
function bestRarityHoldUrl(team: TeamMember[]): string | null {
  let bestUrl: string | null = null;
  let bestRank = -1;
  for (const m of team) {
    const hs = m.holdSkin;
    if (hs && rarityRank(hs.rarity) > bestRank) {
      bestRank = rarityRank(hs.rarity);
      bestUrl = hs.url;
    }
  }
  return bestUrl;
}

/** A 2v2 team shares one lane, so its highest-rarity equipped arena wins. */
function bestRarityBackground(team: TeamMember[]): EquippedCosmetic | null {
  let best: EquippedCosmetic | null = null;
  for (const member of team) {
    const background = member.equipped?.matchBackground;
    if (
      background
      && rarityRank(background.rarity) > rarityRank(best?.rarity)
    ) {
      best = background;
    }
  }
  return best;
}

// Minimum playable width before the lanes feel cramped.
const CANVAS_MIN_W = 320;
// Phones naturally stop near 460px because their viewport is narrow. Tablets
// can use a wider two-lane arena without changing game geometry or timing.
const CANVAS_MAX_W = 760;
const CANVAS_H = LANE_H + HEADER_H;

const COLOR_KEYBINDS: Record<string, ColorType> = {
  r: 'red',
  b: 'blue',
  y: 'yellow',
  p: 'purple',
};

export function MatchScreen() {
  const go = useUIStore((s) => s.go);
  const profile = useUserStore((s) => s.profile);
  const settings = useSettingsStore();

  // Subscribe to all the slices the HUD + game-loop needs
  const yourStep = useMatchStore((s) => s.yourStep);
  const rivalStep = useMatchStore((s) => s.rivalStep);
  const yourPose = useMatchStore((s) => s.yourPose);
  const rivalPose = useMatchStore((s) => s.rivalPose);
  const yourSeq = useMatchStore((s) => s.yourSeq);
  const rivalSeq = useMatchStore((s) => s.rivalSeq);
  const combo = useMatchStore((s) => s.combo);
  const timer = useMatchStore((s) => s.timer);
  const paused = useMatchStore((s) => s.paused);
  const over = useMatchStore((s) => s.over);
  const flashWrong = useMatchStore((s) => s.flashWrong);
  const shield = useMatchStore((s) => s.shield);
  const vortex = useMatchStore((s) => s.vortex);
  const yourDisruptingIndices = useMatchStore((s) => s.yourDisruptingIndices);
  const rivalDisruptingIndices = useMatchStore((s) => s.rivalDisruptingIndices);
  const yourEquipped = useMatchStore((s) => s.yourEquipped);
  const rivalEquipped = useMatchStore((s) => s.rivalEquipped);
  const yourCharacter = useMatchStore((s) => s.yourCharacter);
  const rivalCharacter = useMatchStore((s) => s.rivalCharacter);
  const yourClanTag = useMatchStore((s) => s.yourClanTag);
  const yourClanEmblem = useMatchStore((s) => s.yourClanEmblem);
  const rivalClanTag = useMatchStore((s) => s.rivalClanTag);
  const rivalClanEmblem = useMatchStore((s) => s.rivalClanEmblem);
  const pressedColor = useMatchStore((s) => s.pressedColor);
  const disruptCd = useMatchStore((s) => s.disruptCd);
  const shieldCd = useMatchStore((s) => s.shieldCd);
  const rivalName = useMatchStore((s) => s.rivalName);
  const rivalMmr = useMatchStore((s) => s.rivalMmr);
  const rivalAvatarUrl = useMatchStore((s) => s.rivalAvatarUrl);
  const mode = useMatchStore((s) => s.mode);
  const matchId = useMatchStore((s) => s.matchId);
  const matchSource = useMatchStore((s) => s.matchSource);
  const challengeRun = useMatchStore((s) => s.challengeRun);
  const setChallengeResult = useMatchStore((s) => s.setChallengeResult);
  const rivalId = useMatchStore((s) => s.rivalId);
  const youId = useMatchStore((s) => s.youId);
  const matchmakingPhase = useMatchStore((s) => s.matchmakingPhase);
  const countdownAt = useMatchStore((s) => s.countdownAt);
  const raceActive = matchmakingPhase === 'racing';
  const matchBackgroundTheme = useMemo(
    () => resolveMatchBackgroundTheme(yourEquipped.matchBackground),
    [yourEquipped.matchBackground],
  );

  // ── Team (2v2) slices ──
  const isTeam = useMatchStore((s) => s.isTeam);
  const teamMode = useMatchStore((s) => s.teamMode);
  const turnPlayerId = useMatchStore((s) => s.turnPlayerId);
  const myRole = useMatchStore((s) => s.myRole);
  const myOwnedColors = useMatchStore((s) => s.myOwnedColors);
  const shieldUnlocked = useMatchStore((s) => s.shieldUnlocked);
  const disruptUnlocked = useMatchStore((s) => s.disruptUnlocked);
  const applyTeamStep = useMatchStore((s) => s.applyTeamStep);
  const applyTeamFall = useMatchStore((s) => s.applyTeamFall);
  const applyTeamSkill = useMatchStore((s) => s.applyTeamSkill);
  const applyTeamOver = useMatchStore((s) => s.applyTeamOver);
  const yourTeam = useMatchStore((s) => s.yourTeam);
  const rivalTeam = useMatchStore((s) => s.rivalTeam);
  const leftBackgroundTheme = useMemo(
    () => resolveMatchBackgroundTheme(
      isTeam
        ? bestRarityBackground(yourTeam)
        : yourEquipped.matchBackground,
    ),
    [isTeam, yourTeam, yourEquipped.matchBackground],
  );
  const rightBackgroundTheme = useMemo(
    () => resolveMatchBackgroundTheme(
      isTeam
        ? bestRarityBackground(rivalTeam)
        : rivalEquipped.matchBackground,
    ),
    [isTeam, rivalTeam, rivalEquipped.matchBackground],
  );

  // 2v2: each lane shows its team's HIGHEST-rarity equipped character. Falls
  // back to the single-player character (1v1 / solo) when there is no team.
  // Any climber with NO equipped character (e.g. a server-side matchmaking bot,
  // or an offline bot) falls back to the default STARTER skin (transparent art)
  // instead of the bare default climber PNG — whose baked opaque background
  // tints into a solid colour box. Loaded once and cached.
  const [starterSkin, setStarterSkin] = useState<CharacterSkin | null>(null);
  useEffect(() => { void loadStarterSkin().then(setStarterSkin); }, []);

  const leftCharacter = useMemo(
    () => (isTeam ? bestRarityCharacter(yourTeam) ?? yourCharacter : yourCharacter) ?? starterSkin,
    [isTeam, yourTeam, yourCharacter, starterSkin],
  );
  const rightCharacter = useMemo(
    () => (isTeam ? bestRarityCharacter(rivalTeam) ?? rivalCharacter : rivalCharacter) ?? starterSkin,
    [isTeam, rivalTeam, rivalCharacter, starterSkin],
  );

  // Stable action refs
  const tap = useMatchStore((s) => s.tap);
  const tick = useMatchStore((s) => s.tick);
  const rivalAdvance = useMatchStore((s) => s.rivalAdvance);
  const fireDisrupt = useMatchStore((s) => s.fireDisrupt);
  const fireShield = useMatchStore((s) => s.fireShield);
  // Charge counts drive whether the skill buttons are tappable. Hydrated at
  // mount + after each successful cast so a 0-charge state grays the button.
  const skillsItems = useSkillsStore((s) => s.items);
  const refreshSkills = useSkillsStore((s) => s.refresh);
  const disruptCount = skillsItems.find((s) => s.skill === 'disrupt')?.owned ?? 0;
  const shieldCount = skillsItems.find((s) => s.skill === 'shield')?.owned ?? 0;
  const setPaused = useMatchStore((s) => s.setPaused);
  const setPressedColor = useMatchStore((s) => s.setPressedColor);
  const applyOpponentStep = useMatchStore((s) => s.applyOpponentStep);
  const applyOpponentFall = useMatchStore((s) => s.applyOpponentFall);
  const reconcileYourStep = useMatchStore((s) => s.reconcileYourStep);
  const applyOpponentDisrupt = useMatchStore((s) => s.applyOpponentDisrupt);
  const applyOpponentShield = useMatchStore((s) => s.applyOpponentShield);
  const cancelMyDisrupt = useMatchStore((s) => s.cancelMyDisrupt);
  const notifyMyDisruptAbsorbed = useMatchStore((s) => s.notifyMyDisruptAbsorbed);
  const setOver = useMatchStore((s) => s.setOver);
  const setServerSummary = useMatchStore((s) => s.setServerSummary);

  // Active WebGL chroma-keyed video finisher (admin-uploaded clip), if any.
  const videoFinisherRef = useRef<VideoFinisher | null>(null);
  // True while a media finisher is ringing the bell / playing; defers the
  // VICTORY splash until the clip finishes (onDone opens it).
  const finisherVideoPendingRef = useRef(false);
  // Victory/defeat sting deferred until a video finisher ends, so it doesn't
  // clash with the clip's own audio.
  const pendingStingRef = useRef<'win' | 'lose' | null>(null);

  const sceneRef = useRef<MatchScene | null>(null);
  const startedAtRef = useRef<number>(Date.now());
  const challengeEventsRef = useRef<ChallengeTapEvent[]>([]);
  const challengeSettledRef = useRef(false);

  // Play the winner's equipped finisher: an admin-uploaded full-screen clip
  // wins; otherwise the procedural Pixi sequence (keyed by fxKey). Stable across
  // renders so it can be referenced from the match-over listeners.
  const playFinisher = useCallback(
    (
      winnerSide: 'left' | 'right',
      stompPower: number,
      finisher: { data?: Record<string, unknown> } | null,
    ): boolean => {
      finisherVideoPendingRef.current = false;
      const d = finisher?.data ?? {};
      // A single plain MP4 (green/black background) keyed LIVE by a WebGL shader
      // → transparent over the game on both iOS (WKWebView) and Android. We do
      // NOT rely on video alpha (HEVC/VP9), which WKWebView ignores.
      const clipRaw = d.mediaUrl as string | undefined;
      if (clipRaw) {
        const keyType = (d.keyType as string | undefined) === 'black' ? 'black' : 'green';
        const durationSec = Number(d.durationSec) || 0;
        const maxMs = durationSec > 0 ? durationSec * 1000 : 8000;
        // Cut a bottom strip to hide a baked-in watermark (e.g. KlingAI).
        const cropBottom = d.cutWatermark ? 0.08 : 0;
        // Prefer the preloaded blob (instant + same-origin, no CORS taint) →
        // fall back to streaming the URL.
        const src = getPreloadedMedia(clipRaw) ?? absoluteAssetUrl(clipRaw);
        // Play the classic finish first (bell + stomp + knockdown), hold 0.5s,
        // THEN play the keyed clip on top (game shows through). The VICTORY
        // splash is deferred until the clip's onDone fires.
        finisherVideoPendingRef.current = true;
        sceneRef.current?.playWinSequence(winnerSide, stompPower, null);
        window.setTimeout(() => {
          videoFinisherRef.current?.destroy();
          const vf = new VideoFinisher();
          videoFinisherRef.current = vf;
          vf.play(src, keyType, maxMs, () => {
            if (videoFinisherRef.current === vf) videoFinisherRef.current = null;
            finisherVideoPendingRef.current = false;
            if (pendingStingRef.current) {
              audio.play(pendingStingRef.current === 'win' ? 'match.victory' : 'match.defeat');
              pendingStingRef.current = null;
            }
            setSplashOpen(true);
          }, cropBottom);
        }, 500);
        return true;
      }
      const fxKey = (d.fxKey as string | undefined) ?? null;
      sceneRef.current?.playWinSequence(winnerSide, stompPower, fxKey);
      return false;
    },
    [],
  );
  // Tear down any in-flight video finisher if the screen unmounts mid-play.
  useEffect(() => () => videoFinisherRef.current?.destroy(), []);
  useEffect(() => {
    startedAtRef.current = useMatchStore.getState().raceStartMs ?? Date.now();
    challengeEventsRef.current = [];
    challengeSettledRef.current = false;
    // Pull latest inventory at match start so the buttons show accurate badges.
    void refreshSkills();
  }, [matchId, refreshSkills]);

  // Async challenges enter the normal match screen directly, so this screen
  // owns their 3-2-1 countdown (online matches receive it from Socket.IO).
  useEffect(() => {
    if (matchSource !== 'challenge' || !challengeRun) return;
    const timers = [
      window.setTimeout(() => useMatchStore.setState({ countdownAt: 2 }), 900),
      window.setTimeout(() => useMatchStore.setState({ countdownAt: 1 }), 1800),
      window.setTimeout(() => useMatchStore.setState({ countdownAt: 'GO' }), 2700),
      window.setTimeout(() => {
        const startedAt = Date.now();
        startedAtRef.current = startedAt;
        useMatchStore.setState({ matchmakingPhase: 'racing', countdownAt: null, raceStartMs: startedAt, timer: 0 });
      }, 3150),
    ];
    return () => timers.forEach(window.clearTimeout);
  }, [matchSource, challengeRun?.code]);

  // MATCH_FOUND navigates here before the race starts. Build the wall first,
  // then keep the countdown/start listeners on this mounted screen so the
  // server cannot start gameplay behind a loading overlay.
  useEffect(() => {
    if (mode !== 'online' || !matchId) return;
    const socket = getSocket();
    const onCountdown = ({ at }: MatchCountdown) => {
      useMatchStore.setState({
        matchmakingPhase: 'countdown',
        countdownAt: at === 0 ? 'GO' : at,
      });
      audio.play(at === 0 ? 'match.go' : 'match.countdown');
    };
    const onStart = (_payload: MatchStart) => {
      const startMs = Date.now();
      startedAtRef.current = startMs;
      useMatchStore.setState({
        matchmakingPhase: 'racing',
        countdownAt: null,
        raceStartMs: startMs,
        timer: 0,
      });
    };
    socket.on(EV.MATCH_COUNTDOWN, onCountdown);
    socket.on(EV.MATCH_START, onStart);
    return () => {
      socket.off(EV.MATCH_COUNTDOWN, onCountdown);
      socket.off(EV.MATCH_START, onStart);
    };
  }, [mode, matchId]);

  // ── Drive game timer ────────────────────────────────────────────
  useEffect(() => {
    if (!raceActive || paused || over) return;
    const id = window.setInterval(() => tick(0.1), 100);
    return () => window.clearInterval(id);
  }, [raceActive, paused, over, tick]);

  // ── Knockout haptic ─────────────────────────────────────────────
  // Heavy buzz the moment YOU lose (knocked down off the wall). One effect
  // covers every mode (online 1v1, team, offline) since `over` is the local
  // player's outcome. Native fires Capacitor Haptics; web → navigator.vibrate.
  useEffect(() => {
    if (!over) return;
    if (over === 'lose' && useSettingsStore.getState().haptic) native.haptic('heavy');
    // Screen-wide mood beat — golden flash on win, desaturated red veil on loss.
    // Fires in every mode (online / team / offline) since `over` is set locally.
    sceneRef.current?.playOutcomeFx(over === 'win');
  }, [over]);

  // ── Combo milestone audio ───────────────────────────────────────
  const comboRef = useRef(0);
  useEffect(() => {
    const prev = comboRef.current;
    comboRef.current = combo;
    if (combo <= prev) return; // streak broken or unchanged — no cue
    if (combo === 3) audio.play('match.combo_3');
    else if (combo === 5 || combo === 10 || (combo > 10 && combo % 5 === 0)) {
      audio.play('match.combo_milestone');
    }
  }, [combo]);

  // ── AI bot tick (offline only) ──────────────────────────────────
  useEffect(() => {
    if (!raceActive || mode === 'online' || matchSource === 'challenge' || paused || over) return;
    const ms = Math.max(450, (AI_DEFAULT_SPEED_S + (Math.random() - 0.5) * AI_SPEED_JITTER) * 1000);
    const tk = window.setTimeout(() => rivalAdvance(), ms);
    return () => window.clearTimeout(tk);
  }, [raceActive, mode, matchSource, rivalStep, paused, over, rivalAdvance]);

  // Replay the creator's recorded finish pace as the right-lane ghost. The
  // final scheduled step lands at targetDurationMs; intermediate points use
  // the server-provided eased timeline so the animation remains deterministic.
  useEffect(() => {
    if (matchSource !== 'challenge' || !challengeRun || !raceActive || paused || over) return;
    const interval = window.setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current;
      const due = challengeRun.ghostTimes.filter((time) => time <= elapsed).length;
      let current = useMatchStore.getState().rivalStep;
      while (current < due && !useMatchStore.getState().over) {
        rivalAdvance();
        current += 1;
      }
    }, 40);
    return () => window.clearInterval(interval);
  }, [matchSource, challengeRun, raceActive, paused, over, rivalAdvance]);

  // Settle the local run against the server-validated wall. Challenge matches
  // never alter MMR; the summary only feeds the existing result UI.
  useEffect(() => {
    if (!over || matchSource !== 'challenge' || !challengeRun || challengeSettledRef.current) return;
    challengeSettledRef.current = true;
    void challengeApi.finish(challengeRun.code, challengeRun.ticket, challengeEventsRef.current)
      .then(({ result }) => {
        setChallengeResult(result);
        void reportGameCenterChallengeResult({
          code: result.code,
          durationMs: result.durationMs,
          completed: result.steps >= challengeRun.steps,
        });
        setServerSummary({
          winnerId: result.won ? (youId ?? 'you') : challengeRun.creator.id,
          finalSteps: { you: result.steps, rival: challengeRun.steps },
          durationMs: result.durationMs,
          stats: {
            you: { accuracy: result.accuracy, maxCombo: result.maxCombo, timeMs: result.durationMs },
            rival: { accuracy: 100, maxCombo: challengeRun.steps, timeMs: result.targetDurationMs },
          },
          mmrDelta: { you: 0, rival: 0 },
          rewards: { coins: 0, xp: 0 },
          rankProgression: null,
          winnerShout: null,
          winnerShouts: [],
        });
        if ((over === 'win') !== result.won) setOver(result.won ? 'win' : 'lose');
      })
      .catch((reason) => console.warn('[challenge] settlement failed', reason));
  }, [over, matchSource, challengeRun, setChallengeResult, setServerSummary, setOver, youId]);

  // ── Online: subscribe to opponent + match-over events ───────────
  useEffect(() => {
    if (mode !== 'online' || isTeam) return;
    const socket = getSocket();

    const onStep = (payload: MatchStep) => {
      if (payload.playerId === youId) {
        reconcileYourStep(payload.step, false);
        return;
      }
      if (payload.playerId !== rivalId) return;
      applyOpponentStep(payload.step, payload.pose);
    };
    const onFall = (payload: MatchFall) => {
      if (payload.playerId === youId) {
        reconcileYourStep(payload.step, true);
        return;
      }
      if (payload.playerId !== rivalId) return;
      audio.play('match.fall');
      applyOpponentFall(payload.step);
    };
    const onSkill = (payload: MatchSkillFired) => {
      const isOpp = payload.playerId === rivalId;
      const isMine = payload.playerId === youId;
      if (payload.skill === 'disrupt') {
        if (isOpp) {
          // Opponent disrupted me. Server may have already absorbed it via
          // my active shield — in that case we get the same event back so
          // both sides agree on log + cooldown consumption.
          if (payload.payload?.absorbedBy === 'shield') {
            // No visual disrupt happens to me — I had shield up. (Nothing to
            // do client-side; my shield bubble is already visible.)
            return;
          }
          applyOpponentDisrupt(
            payload.payload?.reshuffleIndices,
            payload.payload?.reshuffleColors,
          );
        } else if (isMine) {
          // My own disrupt: if it was absorbed by opp's pre-existing shield,
          // flash an ABSORBED indicator over the rival lane so I know why
          // their colors didn't change.
          if (payload.payload?.absorbedBy === 'shield') {
            notifyMyDisruptAbsorbed();
            audio.play('match.skill_absorbed');
          }
        }
      } else { // shield
        if (isMine) {
          // My shield cancelled the disrupt mid-flash → abort the local
          // color-apply timer + clear flash UI.
          if (payload.payload?.cancelledDisruptIndices?.length) {
            cancelMyDisrupt();
            audio.play('match.skill_absorbed');
          }
        } else if (isOpp) {
          // Opp shielded → tiny float on their lane.
          applyOpponentShield();
          // If the opp shield cancelled my disrupt, tell me on my lane.
          if (payload.payload?.cancelledDisruptIndices?.length) {
            notifyMyDisruptAbsorbed();
            audio.play('match.skill_absorbed');
          }
        }
      }
    };
    const onOver = (payload: MatchOver) => {
      console.log('[match] received match.over', payload);
      setServerSummary({
        winnerId: payload.winnerId,
        finalSteps: payload.finalSteps,
        durationMs: payload.durationMs,
        stats: payload.stats,
        mmrDelta: payload.mmrDelta,
        rewards: payload.rewards,
        rankProgression: payload.rankProgression ?? null,
        winnerShout: payload.winnerShout ?? null,
        winnerShouts: payload.winnerShout ? [payload.winnerShout] : [],
      });
      // Full dramatic win sequence: bell slam + winner stomp + opponent
      // knocked down the wall + confetti + screen shake. Local player is
      // always the LEFT lane; rival is RIGHT.
      const youWon = payload.winnerId === youId;
      let hasVideo = false;
      try {
        const winnerSide = youWon ? 'left' : 'right';
        const st = useMatchStore.getState();
        const winnerChar = youWon ? st.yourCharacter : st.rivalCharacter;
        const winnerFx = youWon ? st.yourEquipped : st.rivalEquipped;
        hasVideo = playFinisher(winnerSide, winnerChar?.stompPower ?? 1.0, winnerFx?.endgameFx ?? null);
      } catch (e) {
        console.warn('[match] win sequence failed', e);
      }
      // Audio cue. With a video finisher the clip carries its own sound, so we
      // silence the match BGM and DEFER the victory/defeat sting until the clip
      // ends (avoids two soundtracks clashing). Otherwise play it as usual.
      if (hasVideo) {
        audio.stopBgm('bgm.match');
        pendingStingRef.current = youWon ? 'win' : 'lose';
      } else {
        window.setTimeout(() => audio.play(youWon ? 'match.victory' : 'match.defeat'), 800);
      }
      setOver(youWon ? 'win' : 'lose');
    };

    socket.on(EV.MATCH_STEP, onStep);
    socket.on(EV.MATCH_FALL, onFall);
    socket.on(EV.MATCH_SKILL_FIRED, onSkill);
    socket.on(EV.MATCH_OVER, onOver);

    return () => {
      socket.off(EV.MATCH_STEP, onStep);
      socket.off(EV.MATCH_FALL, onFall);
      socket.off(EV.MATCH_SKILL_FIRED, onSkill);
      socket.off(EV.MATCH_OVER, onOver);
    };
  }, [
    mode,
    rivalId,
    youId,
    applyOpponentStep,
    applyOpponentFall,
    reconcileYourStep,
    applyOpponentDisrupt,
    applyOpponentShield,
    cancelMyDisrupt,
    notifyMyDisruptAbsorbed,
    setOver,
    setServerSummary,
  ]);

  // ── Online team (2v2): subscribe to team events ─────────────────
  useEffect(() => {
    if (mode !== 'online' || !isTeam || !youId) return;
    const socket = getSocket();

    const onTeamStep = (payload: TeamMatchStep) => {
      const mine = payload.side === useMatchStore.getState().yourSide;
      if (!mine) audio.play('match.tap_correct');
      applyTeamStep(payload, youId);
    };
    const onTeamFall = (payload: TeamMatchFall) => {
      audio.play('match.fall');
      applyTeamFall(payload, youId);
    };
    const onTeamSkill = (payload: TeamSkillFired) => {
      audio.play(payload.skill === 'shield' ? 'match.skill_shield' : 'match.skill_disrupt');
      applyTeamSkill(payload, youId);
    };
    const onTeamOver = (payload: TeamMatchOver) => {
      applyTeamOver(payload, youId);
      const youWon = payload.winnerSide === useMatchStore.getState().yourSide;
      let hasVideo = false;
      try {
        const winnerSide = youWon ? 'left' : 'right';
        const st = useMatchStore.getState();
        const winnerChar = youWon ? st.yourCharacter : st.rivalCharacter;
        const winnerFx = youWon ? st.yourEquipped : st.rivalEquipped;
        hasVideo = playFinisher(winnerSide, winnerChar?.stompPower ?? 1.0, winnerFx?.endgameFx ?? null);
      } catch (e) {
        console.warn('[team] win sequence failed', e);
      }
      if (hasVideo) {
        audio.stopBgm('bgm.match');
        pendingStingRef.current = youWon ? 'win' : 'lose';
      } else {
        window.setTimeout(() => audio.play(youWon ? 'match.victory' : 'match.defeat'), 800);
      }
    };

    socket.on(EV.TEAM_MATCH_STEP, onTeamStep);
    socket.on(EV.TEAM_MATCH_FALL, onTeamFall);
    socket.on(EV.TEAM_SKILL_FIRED, onTeamSkill);
    socket.on(EV.TEAM_MATCH_OVER, onTeamOver);

    return () => {
      socket.off(EV.TEAM_MATCH_STEP, onTeamStep);
      socket.off(EV.TEAM_MATCH_FALL, onTeamFall);
      socket.off(EV.TEAM_SKILL_FIRED, onTeamSkill);
      socket.off(EV.TEAM_MATCH_OVER, onTeamOver);
    };
  }, [mode, isTeam, youId, applyTeamStep, applyTeamFall, applyTeamSkill, applyTeamOver]);

  // ── Keyboard input ──────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      const color = COLOR_KEYBINDS[k];
      if (color) {
        onTapColor(color);
        return;
      }
      if (k === '1') onFireDisrupt();
      if (k === '2') onFireShield();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, matchId]);

  // ── Bell cinematic (3s) → splash overlay (15s) → result ──────────
  // Flow:
  //   1. Match ends → bell animation already triggered in onOver.
  //   2. After BELL_CINEMATIC_MS (3000), unhide the VICTORY/DEFEAT splash.
  //   3. The splash shows SUMMARY button + a 15s countdown; either action
  //      navigates to the full ResultScreen.
  const [splashOpen, setSplashOpen] = useState(false);
  useEffect(() => {
    if (!over) return;
    // A media finisher defers the splash: it opens from the clip's onDone so the
    // result screen appears AFTER the video, not during it.
    if (finisherVideoPendingRef.current) return;
    const t1 = window.setTimeout(() => setSplashOpen(true), 3000);
    return () => { window.clearTimeout(t1); };
  }, [over]);
  void MATCH_OVER_OVERLAY_MS;

  // ── Emit match.leave on unmount if the match never finished ─────
  useEffect(() => {
    return () => {
      if (mode === 'online' && matchId && !useMatchStore.getState().over) {
        const socket = getSocket();
        socket.emit(EV.MATCH_LEAVE, { matchId });
      }
    };
  }, [mode, matchId]);

  // ── Sync Zustand → Pixi scene ───────────────────────────────────
  // Every call is wrapped in try/catch so an opponent-triggered race (e.g.
  // a Disrupt that lands while a state update is mid-flight) can't crash the
  // whole React tree. A single warning to the console is plenty for debugging.
  const safeScene = (fn: (scene: MatchScene) => void): void => {
    const s = sceneRef.current;
    if (!s) return;
    try {
      fn(s);
    } catch (e) {
      console.warn('[match] scene op threw', e);
    }
  };

  useEffect(() => {
    safeScene((s) => s.setStep('left', yourStep));
  }, [yourStep]);
  useEffect(() => {
    safeScene((s) => s.setStep('right', rivalStep));
  }, [rivalStep]);
  useEffect(() => {
    safeScene((s) => s.setPose('left', yourPose));
  }, [yourPose]);
  useEffect(() => {
    safeScene((s) => s.setPose('right', rivalPose));
  }, [rivalPose]);
  useEffect(() => {
    safeScene((s) => s.setShield(shield));
  }, [shield]);
  useEffect(() => {
    safeScene((s) => s.setVortex(vortex));
  }, [vortex]);
  useEffect(() => {
    safeScene((s) => s.setRivalSeq(rivalSeq));
  }, [rivalSeq]);
  useEffect(() => {
    safeScene((s) => s.setYourSeq(yourSeq));
  }, [yourSeq]);
  useEffect(() => {
    safeScene((s) => s.setDisruptingIndices('left', yourDisruptingIndices));
  }, [yourDisruptingIndices]);
  useEffect(() => {
    safeScene((s) => s.setDisruptingIndices('right', rivalDisruptingIndices));
  }, [rivalDisruptingIndices]);
  useEffect(() => {
    safeScene((s) => s.setEquipped('left', yourEquipped));
  }, [yourEquipped]);
  useEffect(() => {
    safeScene((s) => s.setEquipped('right', rivalEquipped));
  }, [rivalEquipped]);
  useEffect(() => {
    safeScene((s) => s.setCharacter('left', leftCharacter));
  }, [leftCharacter]);
  useEffect(() => {
    safeScene((s) => s.setCharacter('right', rightCharacter));
  }, [rightCharacter]);
  useEffect(() => {
    safeScene((s) => s.setWrTime(timer, !!over));
  }, [timer, over]);

  // ── Resolve & apply the hold-shape skin per lane ──
  // 1v1: the local player's equipped skin (else random default) on BOTH lanes.
  // 2v2: each lane uses its OWN team's highest-rarity equipped skin; if no one
  // on that team equipped one, a random default. Textures are cached per lane
  // so handleSceneReady can re-apply them after a strict-mode/rematch remount.
  const leftHoldRef = useRef<Texture | null>(null);
  const rightHoldRef = useRef<Texture | null>(null);
  useEffect(() => {
    let cancelled = false;
    leftHoldRef.current = null;
    rightHoldRef.current = null;
    safeScene((s) => { s.setHoldShape(null, 'left'); s.setHoldShape(null, 'right'); });
    void (async () => {
      const store = useHoldSkinStore.getState();
      await store.load();
      const randomDefault = (): string | null => {
        const ds = store.defaultUrls;
        return ds.length ? ds[Math.floor(Math.random() * ds.length)] ?? null : null;
      };
      const leftRaw = isTeam ? (bestRarityHoldUrl(yourTeam) ?? randomDefault()) : store.resolveMatchUrl();
      const rightRaw = isTeam ? (bestRarityHoldUrl(rivalTeam) ?? randomDefault()) : leftRaw;
      const applyLane = async (
        raw: string | null,
        side: 'left' | 'right',
        ref: React.MutableRefObject<Texture | null>,
      ) => {
        const url = resolveAssetUrl(raw);
        if (!url) return;
        try {
          const tex = await loadHoldSkinTexture(url);
          if (cancelled || !tex) return;
          ref.current = tex;
          safeScene((s) => s.setHoldShape(tex, side));
        } catch (e) {
          console.warn('[match] hold skin load failed', e);
        }
      };
      await Promise.all([
        applyLane(leftRaw, 'left', leftHoldRef),
        applyLane(rightRaw, 'right', rightHoldRef),
      ]);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, isTeam, yourTeam, rivalTeam]);

  // ── Replay FX via store subscribers ────────────────────────────
  useEffect(() => {
    let prevBursts: BurstFx[] = useMatchStore.getState().bursts;
    let prevFloats: FloatText[] = useMatchStore.getState().floaties;
    const unsub = useMatchStore.subscribe((st) => {
      if (st.bursts !== prevBursts) {
        const added = st.bursts.filter((b) => !prevBursts.find((p) => p.id === b.id));
        added.forEach((b) => sceneRef.current?.spawnTapBurst(b.side, b.color, st.combo));
        prevBursts = st.bursts;
      }
      if (st.floaties !== prevFloats) {
        const added = st.floaties.filter((f) => !prevFloats.find((p) => p.id === f.id));
        added.forEach((f) => {
          const colorName: ColorType | 'cyan' = f.color.includes('cyan')
            ? 'cyan'
            : f.color.includes('red')
              ? 'red'
              : f.color.includes('yellow')
                ? 'yellow'
                : f.color.includes('purple')
                  ? 'purple'
                  : 'blue';
          sceneRef.current?.spawnFloat(f.side, f.text, colorName);
        });
        prevFloats = st.floaties;
      }
    });
    return () => unsub();
  }, []);

  // ── Team input gating ───────────────────────────────────────────
  // Whose turn it is to tap the wall (turn-based modes) / which role taps.
  const canTapColors = raceActive && (
    !isTeam
      ? true
      : teamMode === 'attack-defense'
        ? myRole === 'climber'
        : turnPlayerId === youId
  );
  // attack-defense: only the attacker holds skills; other modes: both can cast
  // once the team combo unlocks them.
  const skillRoleOk = !isTeam || teamMode !== 'attack-defense' || myRole === 'attacker';
  const disruptLocked = !raceActive || (isTeam && (!skillRoleOk || !disruptUnlocked));
  const shieldLocked = !raceActive || (isTeam && (!skillRoleOk || !shieldUnlocked));

  // ── Action helpers (consistent for keyboard + touch) ────────────
  const onTapColor = (c: ColorType) => {
    if (!canTapColors) { audio.play('ui.error'); return; }
    setPressedColor(c);
    if (isTeam) {
      // Server-authoritative: the shared lane advances only on TEAM_MATCH_STEP.
      // Skip local prediction (it would double-advance) — just relay the tap.
      if (mode === 'online' && matchId) {
        const socket = getSocket();
        const t = Date.now() - startedAtRef.current;
        socket.emit(EV.MATCH_TAP, { matchId, step: useMatchStore.getState().yourStep, color: c, t });
      }
      if (settings.haptic) native.haptic('light');
      window.setTimeout(() => setPressedColor(null), 100);
      return;
    }
    if (matchSource === 'challenge') {
      challengeEventsRef.current.push({ color: c, t: Math.max(0, Date.now() - startedAtRef.current) });
    }
    const result = tap(c);
    // Audio cue based on whether the local prediction accepted the tap.
    // 'shielded' = neutral hit so suppress the wrong-tap sting.
    if (result === 'correct') {
      audio.play('match.tap_correct');
      if (settings.haptic) native.haptic('light');
    } else if (result === 'wrong') {
      audio.play('match.tap_wrong');
      if (settings.haptic) native.haptic('warning');
    }
    if (mode === 'online' && matchId) {
      const socket = getSocket();
      const t = Date.now() - startedAtRef.current;
      socket.emit(EV.MATCH_TAP, {
        matchId,
        step: useMatchStore.getState().yourStep,
        color: c,
        t,
      });
    }
    window.setTimeout(() => setPressedColor(null), 100);
  };

  const onFireDisrupt = () => {
    if (disruptLocked) { audio.play('ui.error'); return; }
    if (mode === 'online' && disruptCount <= 0) { audio.play('ui.error'); return; }
    if (isTeam) {
      // No local prediction for team disrupt — the server echoes TEAM_SKILL_FIRED.
      if (mode === 'online' && matchId) {
        const socket = getSocket();
        const t = Date.now() - startedAtRef.current;
        socket.emit(EV.MATCH_SKILL, { matchId, skill: 'disrupt', t });
        window.setTimeout(() => { void refreshSkills(); }, 600);
      }
      return;
    }
    const fired = fireDisrupt();
    if (fired) audio.play('match.skill_disrupt');
    if (fired && mode === 'online' && matchId) {
      const socket = getSocket();
      const t = Date.now() - startedAtRef.current;
      socket.emit(EV.MATCH_SKILL, { matchId, skill: 'disrupt', t });
      window.setTimeout(() => { void refreshSkills(); }, 600);
    }
  };
  const onFireShield = () => {
    if (shieldLocked) { audio.play('ui.error'); return; }
    if (mode === 'online' && shieldCount <= 0) { audio.play('ui.error'); return; }
    if (isTeam) {
      if (mode === 'online' && matchId) {
        const socket = getSocket();
        const t = Date.now() - startedAtRef.current;
        socket.emit(EV.MATCH_SKILL, { matchId, skill: 'shield', t });
        window.setTimeout(() => { void refreshSkills(); }, 600);
      }
      return;
    }
    const fired = fireShield();
    if (fired) audio.play('match.skill_shield');
    if (fired && mode === 'online' && matchId) {
      const socket = getSocket();
      const t = Date.now() - startedAtRef.current;
      socket.emit(EV.MATCH_SKILL, { matchId, skill: 'shield', t });
      window.setTimeout(() => { void refreshSkills(); }, 600);
    }
  };

  // Measure the available width ONCE so the Pixi canvas + lanes always fit the
  // device. The phone bezel was removed so the viewport may be 320–480px+. We
  // intentionally do NOT reactively resize during a match — that would remount
  // the canvas, destroy the scene, and the climber would freeze on the next tap
  // because the new scene starts at step 0 with no replay.
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [canvasW, setCanvasW] = useState<number | null>(null);
  const [stageScale, setStageScale] = useState(1);
  const [tabletLayout, setTabletLayout] = useState(false);
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const initial = el.clientWidth - 20;
    setTabletLayout(el.clientWidth >= 600);
    setCanvasW(Math.max(CANVAS_MIN_W, Math.min(CANVAS_MAX_W, initial)));
    // Vertical fit: the stage flexes to fill the space left between the top
    // bar and the bottom controls. On short viewports (iPhone 15 Safari, where
    // the dynamic toolbar eats height) the fixed-height canvas is taller than
    // that space — scale it down so the bottom color buttons are never clipped.
    const availH = el.clientHeight;
    if (availH > 0) setStageScale(Math.min(1, availH / CANVAS_H));
  }, []);

  // While we still need a numeric width before the canvas mounts, treat null
  // as "not yet measured" and render with a safe default size hidden via a
  // wrapper that won't display until canvasW lands. This avoids a remount.
  const w = canvasW ?? 360;

  // Lane widths must match what MatchScene calculates internally.
  const wrW = 26;
  const gap = 4;
  const laneW = Math.floor((w - wrW - gap * 2) / 2);
  const tapZoneScale = settings.tapZone / 100;
  const discSize = useMemo(
    () => (tabletLayout ? 88 : 60) * tapZoneScale,
    [tabletLayout, tapZoneScale],
  );

  // Once the scene is initialized, replay the current store state so a freshly
  // mounted Pixi scene catches up to wherever the match already is (handles
  // React-strict double-mount and any later remount).
  const handleSceneReady = (scene: MatchScene) => {
    const s = useMatchStore.getState();
    // `force: true` makes setStep apply position even if step matches the
    // scene's freshly-initialized step (0). Required on rematch: the new
    // climber is created at the lane's default origin, not at path[0] — we
    // need an explicit setStep to put it where the store expects.
    scene.setStep('left', s.yourStep, true);
    scene.setStep('right', s.rivalStep, true);
    scene.setPose('left', s.yourPose);
    scene.setPose('right', s.rivalPose);
    scene.setShield(s.shield);
    scene.setVortex(s.vortex);
    scene.setYourSeq(s.yourSeq);
    scene.setRivalSeq(s.rivalSeq);
    scene.setEquipped('left', s.yourEquipped);
    scene.setEquipped('right', s.rivalEquipped);
    // 2v2 lanes show each team's best character; 1v1 uses the single skins.
    scene.setCharacter('left', leftCharacter);
    scene.setCharacter('right', rightCharacter);
    scene.setDisruptingIndices('left', s.yourDisruptingIndices);
    scene.setDisruptingIndices('right', s.rivalDisruptingIndices);
    scene.setWrTime(s.timer, !!s.over);
    // Re-apply each lane's hold skin if it resolved before this (re)mount.
    scene.setHoldShape(leftHoldRef.current, 'left');
    scene.setHoldShape(rightHoldRef.current, 'right');

    // Tell the server our scene is loaded so it can release the countdown once
    // BOTH players are ready → the race starts in sync on both screens. Online
    // only; offline drives its own local countdown.
    if (mode === 'online' && matchId) {
      try { getSocket().emit(EV.MATCH_READY, { matchId }); } catch { /* socket not ready */ }
    }
  };

  const t = useT();

  return (
    <div
      className="screen scanlines match-screen"
      style={{
        background: matchBackgroundTheme.cssBackground,
        animation: flashWrong ? 'shake 0.22s' : 'none',
      }}
    >
      {flashWrong && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(230,57,70,0.18)',
            pointerEvents: 'none',
            zIndex: 25,
          }}
        />
      )}

      <MatchTopBar
        timer={timer}
        combo={combo}
        paused={paused}
        onPause={() => setPaused(!paused)}
        onQuit={() => {
          if (mode === 'online' && matchId) {
            const socket = getSocket();
            socket.emit(EV.MATCH_LEAVE, { matchId });
          }
          go('home');
        }}
      />

      {!raceActive && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 70,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            background: 'rgba(5,3,20,0.62)',
            backdropFilter: 'blur(3px)',
            pointerEvents: 'all',
          }}
        >
          <div
            className="display"
            style={{
              color: 'var(--c-yellow)',
              fontSize: countdownAt == null ? 18 : 76,
              lineHeight: 1,
              letterSpacing: countdownAt == null ? '0.12em' : '0.02em',
              textShadow: '0 0 24px rgba(244,199,36,0.65)',
            }}
          >
            {countdownAt ?? t('common.loading')}
          </div>
          <div
            className="display"
            style={{
              color: 'var(--ink-dim)',
              fontSize: 12,
              letterSpacing: '0.16em',
            }}
          >
            {t('mm.ready_title')}
          </div>
        </div>
      )}

      <div
        ref={stageRef}
        className="match-stage"
        style={{
          position: 'relative',
          flex: '1 1 auto',
          minHeight: 0,
          padding: '8px 10px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: w,
            visibility: canvasW ? 'visible' : 'hidden',
            transform: stageScale < 1 ? `scale(${stageScale})` : undefined,
            transformOrigin: 'center center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: laneW,
              zIndex: 6,
            }}
          >
            <LaneHeader
              side="left"
              color="red"
              name={profile.name}
              mmr={profile.mmr}
              step={yourStep}
              total={yourSeq.length}
              width={laneW}
              avatarFrame={yourEquipped.avatarFrame}
              photoUrl={profile.avatarUrl}
              characterUrl={profile.characterIdleUrl ?? yourCharacter?.idleUrl ?? null}
              clanTag={yourClanTag}
              clanEmblem={yourClanEmblem}
            />
          </div>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: laneW + wrW + gap * 2,
              width: laneW,
              zIndex: 6,
            }}
          >
            <LaneHeader
              side="right"
              color="blue"
              name={rivalName}
              mmr={rivalMmr}
              step={rivalStep}
              total={rivalSeq.length}
              width={laneW}
              avatarFrame={rivalEquipped.avatarFrame}
              photoUrl={rivalAvatarUrl}
              characterUrl={rivalCharacter?.idleUrl ?? null}
              clanTag={rivalClanTag}
              clanEmblem={rivalClanEmblem}
            />
          </div>

          {canvasW != null && (
            <PixiCanvas
              width={canvasW}
              height={CANVAS_H}
              sceneRef={sceneRef}
              onReady={handleSceneReady}
              leftBackgroundTheme={leftBackgroundTheme}
              rightBackgroundTheme={rightBackgroundTheme}
            />
          )}
        </div>
      </div>

      {isTeam && (
        <div className="match-secondary" style={{ padding: '0 16px', marginTop: 'auto' }}>
          <div
            className="display"
            style={{
              textAlign: 'center',
              fontSize: 11,
              letterSpacing: '0.08em',
              padding: '6px 10px',
              borderRadius: 8,
              border: `1px solid ${canTapColors ? 'var(--c-cyan)' : 'var(--hairline)'}`,
              background: canTapColors ? 'rgba(34,211,238,0.12)' : 'rgba(0,0,0,0.3)',
              color: canTapColors ? 'var(--c-cyan)' : 'var(--ink-dim)',
            }}
          >
            {teamMode === 'attack-defense'
              ? (myRole === 'climber' ? t('match.team.you_climb') : t('match.team.you_cast'))
              : (canTapColors ? t('match.team.your_turn') : t('match.team.teammate_turn'))}
          </div>
        </div>
      )}

      {!isTeam && (
        <div className="match-secondary" style={{ padding: '0 16px', marginTop: 'auto' }}>
          <TipTicker />
        </div>
      )}

      <div
        className="match-controls"
        style={{
          padding: '12px 16px calc(16px + env(safe-area-inset-bottom, 0px))',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexDirection: settings.lefty ? 'row-reverse' : 'row',
          flexShrink: 0,
          marginTop: isTeam ? 8 : 0,
        }}
      >
        <SkillButton
          icon={<SkillIcon skill="disrupt" size={48} />}
          cd={disruptCd}
          onFire={onFireDisrupt}
          color="var(--c-purple)"
          label="DISRUPT"
          count={mode === 'online' ? disruptCount : undefined}
          locked={disruptLocked}
        />
        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
            justifyItems: 'center',
          }}
        >
          {(['red', 'blue', 'yellow', 'purple'] as const).map((c) => {
            // In split-color mode only your two colors are tappable.
            const ownsColor = !isTeam || teamMode !== 'split-color' || !myOwnedColors || myOwnedColors.includes(c);
            const padActive = canTapColors && ownsColor;
            return (
            <button
              key={c}
              className={`color-disc color-disc--${c}`}
              data-pressed={pressedColor === c}
              // Single pointer event covers mouse, touch and stylus on every
              // modern browser (including iOS Safari). Using onMouseDown +
              // onTouchStart together caused iOS to fire BOTH for one tap →
              // first tap counted as correct, second tap as wrong, climber
              // appeared not to move.
              onPointerDown={(e) => {
                e.preventDefault();
                if (!padActive) return;
                onTapColor(c);
              }}
              // Disable iOS double-tap zoom & callout menu on the disc.
              style={{
                width: discSize,
                height: discSize,
                touchAction: 'manipulation',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                opacity: padActive ? 1 : 0.35,
                filter: padActive ? 'none' : 'grayscale(0.6)',
                cursor: padActive ? 'pointer' : 'not-allowed',
              }}
              aria-label={t('match.tap_color', { color: c })}
            />
            );
          })}
        </div>
        <SkillButton
          icon={<SkillIcon skill="shield" size={48} />}
          cd={shieldCd}
          onFire={onFireShield}
          color="var(--c-cyan)"
          label="SHIELD"
          count={mode === 'online' ? shieldCount : undefined}
          locked={shieldLocked}
        />
      </div>

      {over && splashOpen && (
        <FinishSplash
          over={over}
          victoryText={
            (over === 'win'
              ? (yourEquipped.victoryShout?.data?.text as string | undefined) ??
                yourEquipped.victoryShout?.name
              : (rivalEquipped.victoryShout?.data?.text as string | undefined) ??
                rivalEquipped.victoryShout?.name) ?? undefined
          }
          onSummary={() => go('result', over)}
        />
      )}

      <span hidden aria-hidden="true">{yourSeq.length}</span>
    </div>
  );
}

interface FinishSplashProps {
  over: 'win' | 'lose';
  victoryText?: string;
  onSummary: () => void;
}

const SUMMARY_AUTO_MS = 15000;

function FinishSplash({ over, victoryText, onSummary }: FinishSplashProps) {
  const t = useT();
  const [remaining, setRemaining] = useState(SUMMARY_AUTO_MS);
  useEffect(() => {
    const start = Date.now();
    const id = window.setInterval(() => {
      const left = SUMMARY_AUTO_MS - (Date.now() - start);
      if (left <= 0) {
        window.clearInterval(id);
        onSummary();
        return;
      }
      setRemaining(left);
    }, 100);
    return () => window.clearInterval(id);
  }, [onSummary]);

  const secs = Math.ceil(remaining / 1000);
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 60,
        background: 'rgba(5,3,20,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          className="display"
          style={{
            fontSize: 80,
            color: over === 'win' ? 'var(--c-yellow)' : 'var(--c-red)',
            textShadow:
              over === 'win'
                ? '0 0 30px rgba(244,199,36,0.7), 0 6px 0 rgba(0,0,0,0.6)'
                : '0 0 30px rgba(230,57,70,0.7), 0 6px 0 rgba(0,0,0,0.6)',
          }}
        >
          {over === 'win' ? t('match.you_win') : t('match.you_lose')}
        </div>
        {victoryText && (
          <div
            className="display"
            style={{
              marginTop: 12,
              padding: '8px 18px',
              display: 'inline-block',
              fontSize: 18,
              letterSpacing: '0.08em',
              color: over === 'win' ? 'var(--c-yellow)' : 'var(--c-cyan)',
              background: 'rgba(0,0,0,0.45)',
              border: `1px solid ${over === 'win' ? 'rgba(244,199,36,0.6)' : 'rgba(34,211,238,0.5)'}`,
              borderRadius: 10,
              textShadow: '0 2px 0 rgba(0,0,0,0.5)',
              animation: 'count-pop 0.5s ease-out',
            }}
          >
            “{victoryText}”
          </div>
        )}
        <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
          <button
            onClick={onSummary}
            className="btn btn-primary"
            style={{ fontSize: 16, padding: '12px 26px', minWidth: 200 }}
          >
            {t('match.summary')}
          </button>
          <div style={{ fontSize: 10, color: 'var(--ink-muted)', fontFamily: 'var(--f-mono)' }}>
            {t('match.auto_continue', { secs })}
          </div>
        </div>
      </div>
    </div>
  );
}
